import type { OrderProperties } from '@src/ampli-web'
import { getEnglishFieldValue, getLangs } from '@src/i18n/locale.util'
import { convertCentsToFloat, getCartAmounts, getLineItemAttribute } from '@src/utils/cart.util'
import { getLineItemsDiscountKeys } from '@src/utils/getLineItemInfo'

/**
 * Returns object of common page URL properties used across all Amplitude page view events.
 */
export function getPageURLProperties(href: string) {
  const url = new URL(href)
  const slug = url.pathname.split('/').slice(-1)[0]
  const searchParams = url.searchParams
  const langs = getLangs()
  return {
    'page path': url.pathname.toLowerCase(),
    'page slug': ['', ...langs].includes(slug) ? '(home)' : slug.toLowerCase(),
    'page url': url.href.toLowerCase(),
    'query params': searchParams.toString() || 'N/A',
    'web source code': searchParams.get('code')?.toLowerCase() ?? undefined,
  }
}

export function getPageGlobalProps(href: string) {
  const pageUrlProps = getPageURLProperties(href)
  return {
    'page slug': pageUrlProps['page slug'],
    'page path': pageUrlProps['page path'],
    'page url': pageUrlProps['page url'],
  }
}

/**
 * Convert a cart/order line item to a line item object expected by Amplitude.
 *
 * @param lineItem A line item from a commercetools cart/order.
 */
export function convertLineItemForAmplitude(lineItem: Commercetools.LineItem) {
  const displayName = getEnglishFieldValue(getLineItemAttribute(lineItem, 'displayName'))
  const description = getEnglishFieldValue(getLineItemAttribute(lineItem, 'description'))
  const productSubtype = getLineItemAttribute(lineItem, 'productSubtype')
  const subscriptionFrequencyCadence = getLineItemAttribute(
    lineItem,
    'subscriptionFrequencyCadence'
  )

  return {
    'product key': lineItem.productKey ?? 'unknown',
    'product type': lineItem.productType.obj?.name ?? 'unknown',
    'product subtype': productSubtype,
    quantity: lineItem.quantity,
    sku: lineItem.variant.sku ?? '',
    description: description ?? 'unknown',
    'subscription frequency cadence': subscriptionFrequencyCadence,
    'display name': displayName ?? 'unknown',
    'royalty code': lineItem.custom?.fields?.royaltyCode ?? 'N/A',
    'bundle id': lineItem.custom?.fields?.bundleId ?? 'N/A',
  }
}

/**
 * Convert a cart/order line item to a line item object expected by Google Tag Manager.
 *
 * @param lineItem A line item from a commercetools cart/order.
 */
export function convertLineItemForGTM(lineItem: Commercetools.LineItem) {
  const description = getEnglishFieldValue(getLineItemAttribute(lineItem, 'description'))
  const productSubtype = getLineItemAttribute(lineItem, 'productSubtype')

  return {
    item_id: lineItem.variant.sku ?? 'unknown',
    item_name: lineItem.productKey ?? 'unknown',
    item_variant: description ?? 'unknown',
    item_category: lineItem.productType.obj?.name,
    item_category2: productSubtype,
    price: convertCentsToFloat(lineItem.price?.value?.centAmount),
    quantity: lineItem.quantity,
  }
}

/**
 * Retrieves the payment method for Amplitude analytics based on the provided order.
 *
 * @param order - The Commercetools order object, or `null` if no order is provided.
 * @returns A string representing the payment method for Amplitude analytics.
 */
export function getPaymentMethodForAmplitude(order: Commercetools.Order | null) {
  const cardType = order?.paymentInfo?.payments?.[0]?.obj?.custom?.fields?.creditCardType
  if (cardType === 'APPLE PAY') return 'apple pay'
  return 'card'
}

/**
 * Convert an order to an object of properties expected by Amplitude.
 *
 * @param order A commercetools order.
 */
export function convertOrderForAmplitude(order: Commercetools.Order | null): OrderProperties {
  const lineItems = (order?.lineItems ?? []) as Commercetools.LineItem[]
  const discounts = getLineItemsDiscountKeys(lineItems)
  const amounts = getCartAmounts(order)
  const { customerEmailDomain, hasExistingPartner, partnerId, webSourceCode } =
    order?.custom.fields ?? {}
  const referralType = hasExistingPartner ? 'existing partner' : partnerId ? 'new partner' : 'none'
  const promoCodes = order?.discountCodes?.length
    ? order?.discountCodes.map((d) => {
        return {
          /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
          code: d.discountCode.obj?.code || 'unknown',
          'used by': d.discountCode.obj?.custom?.fields?.usedBy || 'unknown',
          /* eslint-enable @typescript-eslint/prefer-nullish-coalescing  */
        }
      })
    : []

  return {
    'web source codes': webSourceCode ? webSourceCode.toLowerCase().split('*|*') : undefined,
    currency: order?.totalPrice.currencyCode ?? 'unknown',
    'customer email domain': customerEmailDomain?.toLowerCase() ?? 'unknown',
    'line items': lineItems.map(convertLineItemForAmplitude),
    'order id': order?.orderNumber ?? 'unknown',
    'order total': parseFloat((amounts.total / 100).toFixed(2)),
    'payment method': getPaymentMethodForAmplitude(order),
    'promo codes': promoCodes,
    'referral type': referralType,
    discounts,
    'shipping method': order?.shippingInfo?.shippingMethodName ?? 'unknown',
    store: order?.store?.key ?? 'unknown',
    'customer flow': order?.custom?.fields?.customerFlow ?? 'unknown',
  }
}
